import { Node, NodeSetting } from '../components/TreeSelect/treeSelect';

/**
 * Flattens implied nodes so that when a parent node has multiple selected children,
 * we create separate cards for each selected child with its parent.
 * This is used for the function/role tree where each role should have its own card.
 *
 * @example
 * // Before flattening:
 * // - function1
 * //   |--- role1
 * //   |--- role2
 *
 * // After flattening:
 * // - function1
 * //   |--- role1
 *
 * // - function1
 * //   |--- role2
 */
export const flattenImpliedNodes = <D, S = NodeSetting>(
  nodes: Node<D, S>[]
): Node<D, S>[] => {
  const flattened: Node<D, S>[] = [];

  nodes.forEach(node => {
    if (node.children && node.children.length > 0) {
      const selectedChildren = node.children.filter(
        child => child.isSelected || child.numOfDecendentsSelected > 0
      );

      if (selectedChildren.length > 1) {
        selectedChildren.forEach(child => {
          const clonedParent: Node<D, S> = {
            ...node,
            children: [child],
            numOfChildSelected: child.isSelected ? 1 : 0,
            numOfDecendentsSelected: child.isSelected
              ? 1
              : child.numOfDecendentsSelected
          };
          flattened.push(clonedParent);
        });
      } else {
        flattened.push(node);
      }
    } else {
      flattened.push(node);
    }
  });

  return flattened;
};

export const shouldFlattenImpliedNodes = <S extends NodeSetting>(
  settings: S[]
): boolean => {
  if (!settings || settings.length === 0) return false;

  for (let i = settings.length - 1; i >= 0; i--) {
    const setting = settings[i];
    if (setting.field && !setting.hideCheckbox) {
      return setting.depthLimit === 1;
    }
  }

  return false;
};
